import { BadgeCardRow } from '@bp/ui-components';
import { contrastRatio, WHITE_RGB } from './colorContrast';
import { getCardTextColor } from './getCardTextColor';
import { parseHexColor } from './parseHexColor';

/**
 * Default-Hintergrund der `BadgeCard`, gespiegelt aus `core/styles/global.scss`
 * (`--color-grey-light`). Wird gebraucht, um die Textfarbe dagegen zu rechnen.
 */
const BADGE_CARD_DEFAULT_BACKGROUND = '#8e949f';

/**
 * Ab diesem Kontrast zu Weiß gilt eine Kartenfarbe als zu hell, um sich noch abzuzeichnen.
 * BadgeCards erscheinen nicht nur auf den dunklen Board-Rastern, sondern auch in Tabellen und
 * Modals über `--color-background` (#f5f5f7) – dort verschwindet eine weiße Karte spurlos.
 */
const MIN_CONTRAST_TO_WHITE = 1.25;
const CARD_BORDER_COLOR = 'var(--color-grey-light)';

const needsBorder = (background: string): boolean => {
  const rgb = parseHexColor(background);
  return rgb !== null && contrastRatio(rgb, WHITE_RGB) < MIN_CONTRAST_TO_WHITE;
};

type BadgeCardColorsInput = {
  colorBySubject: boolean;
  subjectColor: string | null;
  rows: BadgeCardRow[];
  /** Aus den Lehrkraft-Farben abgeleitete Textfarbe der Karte (`badgeCardTextColor`). */
  teacherTextColor: string;
};

type BadgeCardColors = {
  backgroundColor: string | undefined;
  color: string;
  rows: BadgeCardRow[];
  /** Gesetzt, wenn die Karte sonst mit einem hellen Untergrund verschmelzen würde. */
  borderColor: string | undefined;
};

/**
 * Farben für die `BadgeCard`-Darstellung einer Epochenkarte (Kartenstapel, Karte in der Hand).
 *
 * Zwei Dinge sind hier anders als bei den gesteckten Karten im Raster:
 *
 * 1. Die Lehrkraft-Farben liegen nicht als Hintergrund unter der Karte, sondern als Streifen
 *    (`rows.colColors`) darüber – und zwar nur auf den belegten Teilungs-Zeilen. Die Beschriftung
 *    ist vertikal zentriert und kann daher über einer unbedeckten Zeile liegen, wo der helle
 *    Default-Hintergrund durchscheint. Die aus den Lehrkraft-Farben abgeleitete Textfarbe passt
 *    dort nicht und ergibt hellgrauen Text auf hellem Grund. Sie wird deshalb nur übernommen,
 *    wenn die Streifen die Karte vollständig bedecken.
 * 2. Bei aktiver Fachfarbe müssen die Streifen weichen. Im Raster ersetzt die Fachfarbe den
 *    Lehrkraft-Farbverlauf vollständig; blieben die Streifen hier stehen, läge die Beschriftung
 *    über den Lehrkraft-Farben, während ihre Farbe aus der Fachfarbe berechnet wurde.
 */
export const getBadgeCardColors = ({
  colorBySubject,
  subjectColor,
  rows,
  teacherTextColor,
}: BadgeCardColorsInput): BadgeCardColors => {
  if (colorBySubject && subjectColor) {
    return {
      backgroundColor: subjectColor,
      color: getCardTextColor(subjectColor),
      rows: rows.map((row) => ({ ...row, colColors: [] })),
      borderColor: needsBorder(subjectColor) ? CARD_BORDER_COLOR : undefined,
    };
  }

  const fullyCovered = rows.length > 0 && rows.every((row) => row.colColors.length > 0);

  return {
    backgroundColor: undefined,
    color: fullyCovered ? teacherTextColor : getCardTextColor(BADGE_CARD_DEFAULT_BACKGROUND),
    rows,
    borderColor: undefined,
  };
};
